import { useLocalStorage } from "@faro-lotv/app-component-toolbox";
import { Accordion, FaroButton, FaroText, grey } from "@faro-lotv/flat-ui";
import { Stack } from "@mui/material";
import { ReactNode, useCallback, type JSX } from "react";
import { BetaBadge } from "../beta-badge";

interface NewFeatureAlertProps {
  /** Local storage key to track whether the user has dismissed the alert. */
  localStorageKey: string;

  /** The title to show */
  title: ReactNode;

  /** The details to show, when the popup is expanded */
  details: ReactNode;

  /** Whether a beta badge should be shown. */
  isBeta?: boolean;
}

/** @returns a new feature alert */
export function NewFeatureAlert({
  localStorageKey,
  title,
  details,
  isBeta,
}: NewFeatureAlertProps): JSX.Element | null {
  const [hasDismissedAnnouncement, setHasDismissedAnnouncement] =
    useLocalStorage(localStorageKey, false);

  const dismissAnnouncement = useCallback(
    () => setHasDismissedAnnouncement(true),
    [setHasDismissedAnnouncement],
  );

  if (hasDismissedAnnouncement) return null;

  return (
    <Accordion
      title={
        <Stack direction="row" sx={{ gap: 1 }}>
          <FaroText variant="heading14">{title}</FaroText>

          {isBeta && <BetaBadge />}
        </Stack>
      }
      sx={{ background: grey[50] }}
    >
      <Stack>{details}</Stack>
      <Stack sx={{ alignItems: "flex-end" }}>
        <FaroButton variant="ghost" size="s" onClick={dismissAnnouncement}>
          Got it
        </FaroButton>
      </Stack>
    </Accordion>
  );
}
